import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { Plus, Pencil, Trash2, X, Clock, CheckCircle2, XCircle } from "lucide-react";
import formatApiError from "../../lib/apiError";

const API = process.env.REACT_APP_BACKEND_URL;
const ax = () => ({ withCredentials: true });

const CANCELLATION_REASONS = [
  { value: "content_not_available", label: "Content Not Available" },
  { value: "technical_issues",      label: "Technical Issues"      },
];
const reasonLabel = (v) =>
  CANCELLATION_REASONS.find((r) => r.value === v)?.label || v || "";

const DURATIONS = [
  { value: 30,  label: "30 min" },
  { value: 60,  label: "1 hr"   },
  { value: 90,  label: "1.5 hr" },
  { value: 120, label: "2 hr"   },
  { value: 150, label: "2.5 hr" },
  { value: 180, label: "3 hr"   },
  { value: 210, label: "3.5 hr" },
  { value: 240, label: "4 hr"   },
];
const durationLabel = (m) => DURATIONS.find((d) => d.value === m)?.label || "";

const fmt12h = (hhmm) => {
  if (!hhmm) return "";
  const [h, m] = hhmm.split(":").map(Number);
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
};

const EMPTY_FORM = {
  date: "",
  professor_id: "",
  subject_id: "",
  lesson_id: "",
  start_time: "",
  duration_minutes: 60,
  medimagic_used: true,
  remarks: "",
  cancellation_reason: "",
  cancellation_description: "",
};

export default function TimetablePage({ readOnly = false }) {
  const [entries, setEntries]       = useState([]);
  const [professors, setProfessors] = useState([]);
  const [subjects, setSubjects]     = useState([]);
  const [lessons,   setLessons]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");

  const [modal, setModal]           = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [formErr, setFormErr]       = useState("");
  const [saving, setSaving]         = useState(false);

  const [confirmId, setConfirmId]   = useState(null);
  const [deleting, setDeleting]     = useState(false);

  const onChange = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, pRes, sRes, mRes] = await Promise.all([
        axios.get(`${API}/api/timetable`,         ax()),
        axios.get(`${API}/api/faculty`,         ax()),
        axios.get(`${API}/api/subjects`,          ax()),
        axios.get(`${API}/api/nmc-lessons`,       ax()),
      ]);
      setEntries(tRes.data);
      setProfessors(pRes.data);
      setSubjects(sRes.data);
      setLessons(mRes.data);
    } catch (e) {
      setError(formatApiError(e, "Failed to load timetable."));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Auto-fill subject when professor changes; reset lesson on subject change
  const handleProfessorChange = (e) => {
    const profId = e.target.value;
    const prof = professors.find((p) => p.id === profId);
    setForm((f) => ({
      ...f,
      professor_id: profId,
      subject_id: prof?.subject_id || "",
      lesson_id: "",
    }));
  };

  const lessonsForSubject = useMemo(
    () => (form.subject_id ? lessons.filter((l) => l.subject_id === form.subject_id) : []),
    [lessons, form.subject_id]
  );

  const openAdd = () => {
    setForm({ ...EMPTY_FORM, date: new Date().toISOString().slice(0, 10) });
    setFormErr("");
    setModal({ mode: "add", entry: null });
  };
  const openEdit = (t) => {
    setForm({
      date: t.date || "",
      professor_id: t.professor_id || "",
      subject_id: t.subject_id || "",
      lesson_id: t.lesson_id || "",
      start_time: t.start_time || "",
      duration_minutes: t.duration_minutes || 60,
      medimagic_used: !!t.medimagic_used,
      remarks: t.remarks || "",
      cancellation_reason: t.cancellation_reason || "",
      cancellation_description: t.cancellation_description || "",
    });
    setFormErr("");
    setModal({ mode: "edit", entry: t });
  };
  const closeModal = () => { setModal(null); setFormErr(""); };

  const handleSave = async () => {
    if (!form.date)         { setFormErr("Date is required.");      return; }
    if (!form.professor_id) { setFormErr("Please select a faculty member."); return; }
    if (!form.subject_id)   { setFormErr("Selected faculty member has no subject mapped. Update the faculty record first."); return; }
    if (!form.lesson_id)    { setFormErr("Please select a lesson."); return; }
    if (!form.medimagic_used && !form.cancellation_reason) {
      setFormErr("Please select a cancellation reason."); return;
    }

    setSaving(true); setFormErr("");
    const payload = {
      date: form.date,
      professor_id: form.professor_id,
      subject_id: form.subject_id,
      lesson_id: form.lesson_id,
      start_time: form.start_time || null,
      duration_minutes: form.duration_minutes ? Number(form.duration_minutes) : null,
      medimagic_used: form.medimagic_used,
      remarks: form.medimagic_used ? form.remarks : "",
      cancellation_reason: form.medimagic_used ? null : form.cancellation_reason,
      cancellation_description: form.medimagic_used ? "" : form.cancellation_description,
    };
    try {
      if (modal.mode === "add") {
        await axios.post(`${API}/api/timetable`, payload, ax());
      } else {
        await axios.put(`${API}/api/timetable/${modal.entry.id}`, payload, ax());
      }
      closeModal(); fetchAll();
    } catch (e) {
      setFormErr(formatApiError(e, "Something went wrong."));
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try { await axios.delete(`${API}/api/timetable/${confirmId}`, ax()); setConfirmId(null); fetchAll(); }
    catch { setConfirmId(null); }
    finally { setDeleting(false); }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="page-wrap">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">Timetable</h1>
          <p className="page-sub">Log each class — whether Medimagic was used, and the reason if it wasn't.</p>
        </div>
        {!readOnly && (
          <button className="btn-primary" onClick={openAdd} data-testid="add-timetable-btn">
            <Plus size={16} /> Add Entry
          </button>
        )}
      </div>

      {loading ? <div className="table-loading">Loading…</div>
       : error  ? <div className="table-error">{error}</div>
       : (
        <div className="data-card">
          {entries.length === 0 ? (
            <div className="empty-state">
              <Clock size={32} color="#43E0FF" />
              <p>No timetable entries yet. Click <strong>Add Entry</strong> to log a class.</p>
            </div>
          ) : (
            <table className="data-table" data-testid="timetable-table">
              <thead>
                <tr>
                  <th style={{ width: 48 }}>#</th>
                  <th style={{ width: 110 }}>Date</th>
                  <th style={{ width: 110 }}>Time</th>
                  <th>Faculty</th>
                  <th>Subject / Lesson</th>
                  <th style={{ width: 130 }}>Medimagic</th>
                  <th>Reason / Remarks</th>
                  {!readOnly && <th style={{ width: 110 }}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {entries.map((t, i) => (
                  <tr key={t.id} data-testid={`timetable-row-${t.id}`}>
                    <td className="td-num">{i + 1}</td>
                    <td className="td-name">{fmtDate(t.date)}</td>
                    <td className="td-desc">
                      {t.start_time ? (
                        <div>
                          <div className="td-person-name">{fmt12h(t.start_time)}</div>
                          {t.duration_minutes && (
                            <div className="td-person-sub">{durationLabel(t.duration_minutes)}</div>
                          )}
                        </div>
                      ) : t.duration_minutes ? (
                        durationLabel(t.duration_minutes)
                      ) : <span className="td-empty">—</span>}
                    </td>
                    <td className="td-desc">{t.professor_name || <span className="td-empty">—</span>}</td>
                    <td className="td-desc">
                      {t.subject_name ? (
                        <div>
                          <span className="subject-pill">
                            {t.subject_code && <span className="subject-pill-code">{t.subject_code}</span>}
                            {t.subject_name}
                          </span>
                          {t.lesson_name && (
                            <div className="td-person-sub" style={{ marginTop: 4 }}>{t.lesson_name}</div>
                          )}
                        </div>
                      ) : <span className="td-empty">—</span>}
                    </td>
                    <td>
                      {t.medimagic_used ? (
                        <span className="status-pill status-used">
                          <CheckCircle2 size={13} /> Used
                        </span>
                      ) : (
                        <span className="status-pill status-not-used">
                          <XCircle size={13} /> Not used
                        </span>
                      )}
                    </td>
                    <td className="td-desc">
                      {t.medimagic_used ? (
                        t.remarks || <span className="td-empty">—</span>
                      ) : (
                        <div>
                          <div className="td-person-name">{reasonLabel(t.cancellation_reason)}</div>
                          {t.cancellation_description && (
                            <div className="td-person-sub">{t.cancellation_description}</div>
                          )}
                        </div>
                      )}
                    </td>
                    {!readOnly && (
                      <td>
                        <div className="action-btns">
                          <button className="action-edit"   onClick={() => openEdit(t)}        title="Edit"   data-testid={`edit-timetable-${t.id}`}><Pencil size={15} /></button>
                          <button className="action-delete" onClick={() => setConfirmId(t.id)} title="Delete" data-testid={`delete-timetable-${t.id}`}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Add / Edit Modal */}
      {modal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-box modal-box-md" onClick={(e) => e.stopPropagation()} data-testid="timetable-modal">
            <div className="modal-head">
              <h2 className="modal-title">{modal.mode === "add" ? "Add Timetable Entry" : "Edit Entry"}</h2>
              <button className="modal-close" onClick={closeModal}><X size={18} /></button>
            </div>

            {formErr && <div className="form-error" data-testid="form-error">{formErr}</div>}

            <div className="modal-body">
              <div className="field-row">
                <div className="field">
                  <label className="field-label">Date <span className="required">*</span></label>
                  <input
                    type="date"
                    className="field-input"
                    value={form.date}
                    onChange={onChange("date")}
                    data-testid="tt-date-input"
                    autoFocus
                  />
                </div>
                <div className="field">
                  <label className="field-label">Faculty <span className="required">*</span></label>
                  <select
                    className="field-input"
                    value={form.professor_id}
                    onChange={handleProfessorChange}
                    data-testid="tt-faculty-select"
                  >
                    <option value="">Select a faculty member…</option>
                    {professors.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label className="field-label">Subject <span className="field-hint-inline">(auto-filled)</span></label>
                  <input
                    className="field-input"
                    value={
                      form.subject_id
                        ? (() => {
                            const s = subjects.find((x) => x.id === form.subject_id);
                            return s ? (s.code ? `${s.code} — ${s.name}` : s.name) : "";
                          })()
                        : ""
                    }
                    placeholder="Select a faculty member to auto-fill"
                    readOnly
                    data-testid="tt-subject-display"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Lesson <span className="required">*</span></label>
                  <select
                    className="field-input"
                    value={form.lesson_id}
                    onChange={onChange("lesson_id")}
                    disabled={!form.subject_id}
                    data-testid="tt-lesson-select"
                  >
                    <option value="">
                      {form.subject_id ? "Select a lesson…" : "Select a faculty member first"}
                    </option>
                    {lessonsForSubject.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.lesson_code ? `${l.lesson_code} — ${l.name}` : l.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="field-row">
                <div className="field">
                  <label className="field-label">Start Time <span className="field-hint-inline">(optional)</span></label>
                  <input
                    type="time"
                    className="field-input"
                    value={form.start_time}
                    onChange={onChange("start_time")}
                    data-testid="tt-time-input"
                  />
                </div>
                <div className="field">
                  <label className="field-label">Duration</label>
                  <select
                    className="field-input"
                    value={form.duration_minutes}
                    onChange={(e) => setForm((f) => ({ ...f, duration_minutes: Number(e.target.value) }))}
                    data-testid="tt-duration-select"
                  >
                    {DURATIONS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Yes / No toggle */}
              <div className="toggle-card" data-testid="tt-medimagic-used-row">
                <div className="toggle-card-text">
                  <p className="toggle-card-title">Was Medimagic used in this class?</p>
                  <p className="toggle-card-sub">
                    {form.medimagic_used
                      ? "Class delivered with Medimagic — add optional remarks."
                      : "Class skipped Medimagic — pick a reason below."}
                  </p>
                </div>
                <div className="toggle-segmented" role="radiogroup" aria-label="Medimagic used">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={form.medimagic_used}
                    className={`toggle-seg-btn toggle-seg-yes${form.medimagic_used ? " active" : ""}`}
                    onClick={() => setForm((f) => ({ ...f, medimagic_used: true }))}
                    data-testid="tt-medimagic-yes"
                  >
                    <CheckCircle2 size={15} /> Yes
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={!form.medimagic_used}
                    className={`toggle-seg-btn toggle-seg-no${!form.medimagic_used ? " active" : ""}`}
                    onClick={() => setForm((f) => ({ ...f, medimagic_used: false }))}
                    data-testid="tt-medimagic-no"
                  >
                    <XCircle size={15} /> No
                  </button>
                </div>
              </div>

              {/* Conditional fields */}
              {form.medimagic_used ? (
                <div className="field">
                  <label className="field-label">Remarks</label>
                  <textarea
                    className="field-input field-textarea"
                    placeholder="Any notes about this class (optional)"
                    value={form.remarks}
                    onChange={onChange("remarks")}
                    rows={3}
                    maxLength={1000}
                    data-testid="tt-remarks-input"
                  />
                </div>
              ) : (
                <>
                  <div className="field">
                    <label className="field-label">Cancellation Reason <span className="required">*</span></label>
                    <select
                      className="field-input"
                      value={form.cancellation_reason}
                      onChange={onChange("cancellation_reason")}
                      data-testid="tt-cancellation-reason-select"
                    >
                      <option value="">Select a reason…</option>
                      {CANCELLATION_REASONS.map((r) => (
                        <option key={r.value} value={r.value}>{r.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="field">
                    <label className="field-label">Description</label>
                    <textarea
                      className="field-input field-textarea"
                      placeholder="Describe the issue in more detail (optional)"
                      value={form.cancellation_description}
                      onChange={onChange("cancellation_description")}
                      rows={3}
                      maxLength={1000}
                      data-testid="tt-cancellation-description-input"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="modal-foot">
              <button className="btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn-primary" onClick={handleSave} disabled={saving} data-testid="save-timetable-btn">
                {saving ? <span className="btn-spinner" /> : modal.mode === "add" ? "Add Entry" : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="modal-box modal-box-sm" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-modal">
            <div className="modal-head">
              <h2 className="modal-title">Delete Entry</h2>
              <button className="modal-close" onClick={() => setConfirmId(null)}><X size={18} /></button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">Are you sure you want to delete this timetable entry? This action cannot be undone.</p>
            </div>
            <div className="modal-foot">
              <button className="btn-ghost" onClick={() => setConfirmId(null)}>Cancel</button>
              <button className="btn-danger" onClick={handleDelete} disabled={deleting} data-testid="confirm-delete-btn">
                {deleting ? <span className="btn-spinner" /> : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
